import { get, post, del } from 'data/services/apiService';
import { API_URL } from 'data/services/apiConstants';

// Action Types for Property Notifications
const FETCH_PROPERTY_NOTIFICATIONS_REQUEST =
	'FETCH_PROPERTY_NOTIFICATIONS_REQUEST';
const FETCH_PROPERTY_NOTIFICATIONS_SUCCESS =
	'FETCH_PROPERTY_NOTIFICATIONS_SUCCESS';
const FETCH_PROPERTY_NOTIFICATIONS_ERROR = 'FETCH_PROPERTY_NOTIFICATIONS_ERROR';

// Action Types for Property Stats
const FETCH_PROPERTY_STATS_REQUEST = 'FETCH_PROPERTY_STATS_REQUEST';
const FETCH_PROPERTY_STATS_SUCCESS = 'FETCH_PROPERTY_STATS_SUCCESS';
const FETCH_PROPERTY_STATS_ERROR = 'FETCH_PROPERTY_STATS_ERROR';

// Action Types for Property Favorites
const ADD_PROPERTY_TO_FAVORITES_REQUEST = 'ADD_PROPERTY_TO_FAVORITES_REQUEST';
const ADD_PROPERTY_TO_FAVORITES_SUCCESS = 'ADD_PROPERTY_TO_FAVORITES_SUCCESS';
const ADD_PROPERTY_TO_FAVORITES_ERROR = 'ADD_PROPERTY_TO_FAVORITES_ERROR';

const REMOVE_PROPERTY_FROM_FAVORITES_REQUEST =
	'REMOVE_PROPERTY_FROM_FAVORITES_REQUEST';
const REMOVE_PROPERTY_FROM_FAVORITES_SUCCESS =
	'REMOVE_PROPERTY_FROM_FAVORITES_SUCCESS';
const REMOVE_PROPERTY_FROM_FAVORITES_ERROR =
	'REMOVE_PROPERTY_FROM_FAVORITES_ERROR';

// Action Types for Fetching All Favorite Properties
const FETCH_FAVORITE_PROPERTIES_REQUEST = 'FETCH_FAVORITE_PROPERTIES_REQUEST';
const FETCH_FAVORITE_PROPERTIES_SUCCESS = 'FETCH_FAVORITE_PROPERTIES_SUCCESS';
const FETCH_FAVORITE_PROPERTIES_ERROR = 'FETCH_FAVORITE_PROPERTIES_ERROR';

// Action Types for Fetching All Portfolio Properties
const FETCH_PORTFOLIO_PROPERTIES_REQUEST = 'FETCH_PORTFOLIO_PROPERTIES_REQUEST';
const FETCH_PORTFOLIO_PROPERTIES_SUCCESS = 'FETCH_PORTFOLIO_PROPERTIES_SUCCESS';
const FETCH_PORTFOLIO_PROPERTIES_ERROR = 'FETCH_PORTFOLIO_PROPERTIES_ERROR';

const FETCH_PORTFOLIO_PROPERTIES_COUNT_REQUEST =
	'FETCH_PORTFOLIO_PROPERTIES_COUNT_REQUEST';
const FETCH_PORTFOLIO_PROPERTIES_COUNT_SUCCESS =
	'FETCH_PORTFOLIO_PROPERTIES_COUNT_SUCCESS';
const FETCH_PORTFOLIO_PROPERTIES_COUNT_ERROR =
	'FETCH_PORTFOLIO_PROPERTIES_COUNT_ERROR';

// Action Types for Property Portfolios
const FETCH_PROPERTY_PORTFOLIOS_REQUEST = 'FETCH_PROPERTY_PORTFOLIOS_REQUEST';
const FETCH_PROPERTY_PORTFOLIOS_SUCCESS = 'FETCH_PROPERTY_PORTFOLIOS_SUCCESS';
const FETCH_PROPERTY_PORTFOLIOS_ERROR = 'FETCH_PROPERTY_PORTFOLIOS_ERROR';

// Action Types for Property Users
const FETCH_PROPERTY_USERS_REQUEST = 'FETCH_PROPERTY_USERS_REQUEST';
const FETCH_PROPERTY_USERS_SUCCESS = 'FETCH_PROPERTY_USERS_SUCCESS';
const FETCH_PROPERTY_USERS_ERROR = 'FETCH_PROPERTY_USERS_ERROR';

// Utility Actions
const CLEAR_PROPERTY_ERRORS = 'CLEAR_PROPERTY_ERRORS';
const CLEAR_PROPERTY_DATA = 'CLEAR_PROPERTY_DATA';

// Initial State
const initialState = {
	// Property notifications
	notifications: [],
	isLoadingNotifications: false,
	notificationsError: null,
	notificationsTotalCount: 0,
	notificationsCurrentPage: 1,
	notificationsPerPage: 10,

	// Property stats
	stats: {
		openCertificates: 0,
		openViolations: 0,
		openInfrastructure: 0,
	},
	isLoadingStats: false,
	statsError: null,

	// Property favorites
	isFavorite: false,
	isTogglingFavorite: false,
	favoriteError: null,
	favoriteProperties: [], // Array of all favorite properties
	isLoadingFavoriteProperties: false,
	favoritePropertiesError: null,

	// Portfolio properties (all properties from user's portfolio sets)
	portfolioProperties: [], // Array of all portfolio properties
	isLoadingPortfolioProperties: false,
	portfolioPropertiesError: null,

	// Portfolio properties count (org/role-scoped count without the array)
	portfolioPropertiesCount: null,
	isLoadingPortfolioPropertiesCount: false,
	portfolioPropertiesCountError: null,

	// Property portfolios (without pagination)
	portfolios: [],
	isLoadingPortfolios: false,
	portfoliosError: null,

	// Property users (without pagination)
	users: [],
	isLoadingUsers: false,
	usersError: null,

	// General property data
	selectedPropertyId: null,

	// General errors
	error: null,
};

// Action Creators for Property Notifications
export const fetchPropertyNotificationsRequest = () => ({
	type: FETCH_PROPERTY_NOTIFICATIONS_REQUEST,
	payload: {
		isLoadingNotifications: true,
		notificationsError: null,
	},
});

export const fetchPropertyNotificationsSuccess = (data) => ({
	type: FETCH_PROPERTY_NOTIFICATIONS_SUCCESS,
	payload: {
		isLoadingNotifications: false,
		notifications: data.data || [],
		notificationsTotalCount: data.total || 0,
		notificationsCurrentPage: data.current_page || 1,
		notificationsPerPage: data.per_page || 10,
		notificationsError: null,
	},
});

export const fetchPropertyNotificationsError = (error) => ({
	type: FETCH_PROPERTY_NOTIFICATIONS_ERROR,
	payload: {
		isLoadingNotifications: false,
		notificationsError: error,
	},
});

// Action Creators for Property Stats
export const fetchPropertyStatsRequest = () => ({
	type: FETCH_PROPERTY_STATS_REQUEST,
	payload: {
		isLoadingStats: true,
		statsError: null,
	},
});

export const fetchPropertyStatsSuccess = (stats) => ({
	type: FETCH_PROPERTY_STATS_SUCCESS,
	payload: {
		isLoadingStats: false,
		stats: {
			openCertificates: stats.openCertificates || 0,
			openViolations: stats.openViolations || 0,
			openInfrastructure: stats.openInfrastructure || 0,
		},
		statsError: null,
	},
});

export const fetchPropertyStatsError = (error) => ({
	type: FETCH_PROPERTY_STATS_ERROR,
	payload: {
		isLoadingStats: false,
		statsError: error,
	},
});

// Action Creators for Property Favorites
export const addPropertyToFavoritesRequest = () => ({
	type: ADD_PROPERTY_TO_FAVORITES_REQUEST,
	payload: {
		isTogglingFavorite: true,
		favoriteError: null,
	},
});

export const addPropertyToFavoritesSuccess = (favoriteProperties) => ({
	type: ADD_PROPERTY_TO_FAVORITES_SUCCESS,
	payload: {
		isTogglingFavorite: false,
		isFavorite: true,
		favoriteError: null,
		favoriteProperties: favoriteProperties || [],
	},
});

export const addPropertyToFavoritesError = (error) => ({
	type: ADD_PROPERTY_TO_FAVORITES_ERROR,
	payload: {
		isTogglingFavorite: false,
		favoriteError: error,
	},
});

export const removePropertyFromFavoritesRequest = () => ({
	type: REMOVE_PROPERTY_FROM_FAVORITES_REQUEST,
	payload: {
		isTogglingFavorite: true,
		favoriteError: null,
	},
});

export const removePropertyFromFavoritesSuccess = (favoriteProperties) => ({
	type: REMOVE_PROPERTY_FROM_FAVORITES_SUCCESS,
	payload: {
		isTogglingFavorite: false,
		isFavorite: false,
		favoriteError: null,
		favoriteProperties: favoriteProperties || [],
	},
});

export const removePropertyFromFavoritesError = (error) => ({
	type: REMOVE_PROPERTY_FROM_FAVORITES_ERROR,
	payload: {
		isTogglingFavorite: false,
		favoriteError: error,
	},
});

// Action Creators for Fetching All Favorite Properties
export const fetchFavoritePropertiesRequest = () => ({
	type: FETCH_FAVORITE_PROPERTIES_REQUEST,
	payload: {
		isLoadingFavoriteProperties: true,
		favoritePropertiesError: null,
	},
});

export const fetchFavoritePropertiesSuccess = (favoriteProperties) => ({
	type: FETCH_FAVORITE_PROPERTIES_SUCCESS,
	payload: {
		isLoadingFavoriteProperties: false,
		favoriteProperties: favoriteProperties || [],
		favoritePropertiesError: null,
	},
});

export const fetchFavoritePropertiesError = (error) => ({
	type: FETCH_FAVORITE_PROPERTIES_ERROR,
	payload: {
		isLoadingFavoriteProperties: false,
		favoritePropertiesError: error,
	},
});

// Action Creators for Fetching All Portfolio Properties
export const fetchPortfolioPropertiesRequest = () => ({
	type: FETCH_PORTFOLIO_PROPERTIES_REQUEST,
	payload: {
		isLoadingPortfolioProperties: true,
		portfolioPropertiesError: null,
	},
});

export const fetchPortfolioPropertiesSuccess = (portfolioProperties) => ({
	type: FETCH_PORTFOLIO_PROPERTIES_SUCCESS,
	payload: {
		isLoadingPortfolioProperties: false,
		portfolioProperties: portfolioProperties || [],
		portfolioPropertiesError: null,
	},
});

export const fetchPortfolioPropertiesError = (error) => ({
	type: FETCH_PORTFOLIO_PROPERTIES_ERROR,
	payload: {
		isLoadingPortfolioProperties: false,
		portfolioPropertiesError: error,
	},
});

// Action Creators for the Portfolio Properties Count
export const fetchPortfolioPropertiesCountRequest = () => ({
	type: FETCH_PORTFOLIO_PROPERTIES_COUNT_REQUEST,
	payload: {
		isLoadingPortfolioPropertiesCount: true,
		portfolioPropertiesCountError: null,
	},
});

export const fetchPortfolioPropertiesCountSuccess = (count) => ({
	type: FETCH_PORTFOLIO_PROPERTIES_COUNT_SUCCESS,
	payload: {
		isLoadingPortfolioPropertiesCount: false,
		portfolioPropertiesCount: count,
		portfolioPropertiesCountError: null,
	},
});

export const fetchPortfolioPropertiesCountError = (error) => ({
	type: FETCH_PORTFOLIO_PROPERTIES_COUNT_ERROR,
	payload: {
		isLoadingPortfolioPropertiesCount: false,
		portfolioPropertiesCountError: error,
	},
});

// Action Creators for Property Portfolios
export const fetchPropertyPortfoliosRequest = () => ({
	type: FETCH_PROPERTY_PORTFOLIOS_REQUEST,
	payload: {
		isLoadingPortfolios: true,
		portfoliosError: null,
	},
});

export const fetchPropertyPortfoliosSuccess = (portfolios) => ({
	type: FETCH_PROPERTY_PORTFOLIOS_SUCCESS,
	payload: {
		isLoadingPortfolios: false,
		portfolios: portfolios || [],
		portfoliosError: null,
	},
});

export const fetchPropertyPortfoliosError = (error) => ({
	type: FETCH_PROPERTY_PORTFOLIOS_ERROR,
	payload: {
		isLoadingPortfolios: false,
		portfoliosError: error,
	},
});

// Action Creators for Property Users
export const fetchPropertyUsersRequest = () => ({
	type: FETCH_PROPERTY_USERS_REQUEST,
	payload: {
		isLoadingUsers: true,
		usersError: null,
	},
});

export const fetchPropertyUsersSuccess = (users) => ({
	type: FETCH_PROPERTY_USERS_SUCCESS,
	payload: {
		isLoadingUsers: false,
		users: users || [],
		usersError: null,
	},
});

export const fetchPropertyUsersError = (error) => ({
	type: FETCH_PROPERTY_USERS_ERROR,
	payload: {
		isLoadingUsers: false,
		usersError: error,
	},
});

// Thunk Action Creators

// Get Property Notifications with pagination
export const getPropertyNotifications =
	(propertyId, page = 1, perPage = 10, filters = {}) =>
	(dispatch, getState) => {
		const state = getState();
		const url = `${API_URL}/api/properties/${propertyId}/notifications`;
		const params = new URLSearchParams({
			page: page.toString(),
			per_page: perPage.toString(),
			...filters,
		});

		dispatch(fetchPropertyNotificationsRequest());

		return get({
			dispatch,
			url: `${url}?${params.toString()}`,
			authToken: state.user.authToken,
			onSuccess: (result) => {
				dispatch(
					fetchPropertyNotificationsSuccess({
						data: result.data || [],
						total: result.total || 0,
						current_page: result.current_page || 1,
						per_page: result.per_page || perPage,
						total_pages: result.total_pages || 1,
					}),
				);
				return result;
			},
			onError: (error) => {
				console.error('Error fetching property notifications:', error);
				dispatch(fetchPropertyNotificationsError(error));
				throw error;
			},
		});
	};

// Get Property Stats
export const getPropertyStats = (propertyId) => (dispatch, getState) => {
	const state = getState();
	const url = `${API_URL}/api/properties/${propertyId}/stats`;

	dispatch(fetchPropertyStatsRequest());

	return get({
		dispatch,
		url,
		authToken: state.user.authToken,
		onSuccess: (result) => {
			dispatch(fetchPropertyStatsSuccess(result));
			return result;
		},
		onError: (error) => {
			console.error('Error fetching property stats:', error);
			dispatch(fetchPropertyStatsError(error));
			throw error;
		},
	});
};

// Get All Favorite Properties
export const getFavoriteProperties = () => (dispatch, getState) => {
	const state = getState();
	const url = `${API_URL}/api/properties/favorites`;

	dispatch(fetchFavoritePropertiesRequest());

	return get({
		dispatch,
		url,
		authToken: state.user.authToken,
		onSuccess: (result) => {
			dispatch(fetchFavoritePropertiesSuccess(result.favoriteProperties));
			return result;
		},
		onError: (error) => {
			console.error('Error fetching favorite properties:', error);
			dispatch(fetchFavoritePropertiesError(error));
			throw error;
		},
	});
};

// Get All Portfolio Properties
export const getPortfolioProperties = () => (dispatch, getState) => {
	const state = getState();
	const url = `${API_URL}/api/portfolio-sets/properties`;

	dispatch(fetchPortfolioPropertiesRequest());

	return get({
		dispatch,
		url,
		authToken: state.user.authToken,
		onSuccess: (result) => {
			const portfolioProperties = result.data || result || [];
			dispatch(fetchPortfolioPropertiesSuccess(portfolioProperties));
			return result;
		},
		onError: (error) => {
			console.error('Error fetching portfolio properties:', error);
			dispatch(fetchPortfolioPropertiesError(error));
			throw error;
		},
	});
};

// Get the count of Portfolio Properties (org/role-scoped, no array payload)
export const getPortfolioPropertiesCount = () => (dispatch, getState) => {
	const state = getState();
	const url = `${API_URL}/api/portfolio-sets/properties/count`;

	dispatch(fetchPortfolioPropertiesCountRequest());

	return get({
		dispatch,
		url,
		authToken: state.user.authToken,
		onSuccess: (result) => {
			const count = result?.data?.count ?? result?.count ?? 0;
			dispatch(fetchPortfolioPropertiesCountSuccess(count));
			return result;
		},
		onError: (error) => {
			console.error('Error fetching portfolio properties count:', error);
			dispatch(fetchPortfolioPropertiesCountError(error));
			throw error;
		},
	});
};

// Add Property to Favorites
export const addPropertyToFavorites = (propertyId) => (dispatch, getState) => {
	const state = getState();
	const url = `${API_URL}/api/properties/${propertyId}/favorites`;

	dispatch(addPropertyToFavoritesRequest());

	return post({
		dispatch,
		url,
		authToken: state.user.authToken,
		data: {}, // Empty body for POST request
		onSuccess: (result) => {
			dispatch(addPropertyToFavoritesSuccess(result.favoriteProperties));
			return result;
		},
		onError: (error) => {
			console.error('Error adding property to favorites:', error);
			dispatch(addPropertyToFavoritesError(error));
			throw error;
		},
	});
};

// Remove Property from Favorites
export const removePropertyFromFavorites =
	(propertyId) => (dispatch, getState) => {
		const state = getState();
		const url = `${API_URL}/api/properties/${propertyId}/favorites`;

		dispatch(removePropertyFromFavoritesRequest());

		return del({
			dispatch,
			url,
			authToken: state.user.authToken,
			onSuccess: (result) => {
				dispatch(
					removePropertyFromFavoritesSuccess(result?.favoriteProperties || []),
				);
				return result;
			},
			onError: (error) => {
				console.error('Error removing property from favorites:', error);
				dispatch(removePropertyFromFavoritesError(error));
				throw error;
			},
		});
	};

// Toggle Property Favorite (convenience method)
export const togglePropertyFavorite =
	(propertyId, currentIsFavorite) => (dispatch) => {
		if (currentIsFavorite) {
			return dispatch(removePropertyFromFavorites(propertyId));
		} else {
			return dispatch(addPropertyToFavorites(propertyId));
		}
	};

// Get Property Portfolios (without pagination)
export const getPropertyPortfolios = (propertyId) => (dispatch, getState) => {
	const state = getState();
	const url = `${API_URL}/api/properties/${propertyId}/portfolios`;

	dispatch(fetchPropertyPortfoliosRequest());

	return get({
		dispatch,
		url,
		authToken: state.user.authToken,
		onSuccess: (result) => {
			// Extract data from the response - handle both direct array and wrapped data
			const portfolios = result.data || result || [];
			dispatch(fetchPropertyPortfoliosSuccess(portfolios));
			return result;
		},
		onError: (error) => {
			console.error('Error fetching property portfolios:', error);
			dispatch(fetchPropertyPortfoliosError(error));
			throw error;
		},
	});
};

// Get Property Users within a portfolio set (without pagination)
export const getPropertyUsers =
	(portfolioSetId, propertyId) => (dispatch, getState) => {
		const state = getState();
		const url = `${API_URL}/api/portfolio-sets/${portfolioSetId}/properties/${propertyId}/users`;

		dispatch(fetchPropertyUsersRequest());

		return get({
			dispatch,
			url,
			authToken: state.user.authToken,
			onSuccess: (result) => {
				// Extract data from the response - handle both direct array and wrapped data
				const users = result.data || result || [];
				dispatch(fetchPropertyUsersSuccess(users));
				return result;
			},
			onError: (error) => {
				console.error('Error fetching property users:', error);
				dispatch(fetchPropertyUsersError(error));
				throw error;
			},
		});
	};

// Utility Actions
export const clearPropertyErrors = () => ({
	type: CLEAR_PROPERTY_ERRORS,
});

export const clearPropertyData = () => ({
	type: CLEAR_PROPERTY_DATA,
});

// Reducer
const propertyManagementReducer = (state = initialState, action) => {
	switch (action.type) {
		case FETCH_PROPERTY_NOTIFICATIONS_REQUEST:
		case FETCH_PROPERTY_NOTIFICATIONS_SUCCESS:
		case FETCH_PROPERTY_NOTIFICATIONS_ERROR:
		case FETCH_PROPERTY_STATS_REQUEST:
		case FETCH_PROPERTY_STATS_SUCCESS:
		case FETCH_PROPERTY_STATS_ERROR:
		case ADD_PROPERTY_TO_FAVORITES_REQUEST:
		case ADD_PROPERTY_TO_FAVORITES_SUCCESS:
		case ADD_PROPERTY_TO_FAVORITES_ERROR:
		case REMOVE_PROPERTY_FROM_FAVORITES_REQUEST:
		case REMOVE_PROPERTY_FROM_FAVORITES_SUCCESS:
		case REMOVE_PROPERTY_FROM_FAVORITES_ERROR:
		case FETCH_FAVORITE_PROPERTIES_REQUEST:
		case FETCH_FAVORITE_PROPERTIES_SUCCESS:
		case FETCH_FAVORITE_PROPERTIES_ERROR:
		case FETCH_PORTFOLIO_PROPERTIES_REQUEST:
		case FETCH_PORTFOLIO_PROPERTIES_SUCCESS:
		case FETCH_PORTFOLIO_PROPERTIES_ERROR:
		case FETCH_PORTFOLIO_PROPERTIES_COUNT_REQUEST:
		case FETCH_PORTFOLIO_PROPERTIES_COUNT_SUCCESS:
		case FETCH_PORTFOLIO_PROPERTIES_COUNT_ERROR:
		case FETCH_PROPERTY_PORTFOLIOS_REQUEST:
		case FETCH_PROPERTY_PORTFOLIOS_SUCCESS:
		case FETCH_PROPERTY_PORTFOLIOS_ERROR:
		case FETCH_PROPERTY_USERS_REQUEST:
		case FETCH_PROPERTY_USERS_SUCCESS:
		case FETCH_PROPERTY_USERS_ERROR:
			return {
				...state,
				...action.payload,
			};

		case CLEAR_PROPERTY_ERRORS:
			return {
				...state,
				error: null,
				notificationsError: null,
				statsError: null,
				favoriteError: null,
				portfoliosError: null,
				usersError: null,
			};

		case CLEAR_PROPERTY_DATA:
			return {
				...state,
				notifications: [],
				stats: {
					openCertificates: 0,
					openViolations: 0,
					openInfrastructure: 0,
				},
				isFavorite: false,
				selectedPropertyId: null,
				notificationsTotalCount: 0,
				notificationsCurrentPage: 1,
				portfolios: [],
				users: [],
			};

		default:
			return state;
	}
};

export default propertyManagementReducer;
